import { SIMULATION_CONFIG } from "../config/simulationConfig";

const LOG_SQRT_TWO_PI = 0.9189385332046727;
const LANCZOS_COEFFS = [
  676.5203681218851,
  -1259.1392167224028,
  771.3234287776531,
  -176.6150291621406,
  12.507343278686905,
  -0.13857109526572012,
  9.984369578019572e-6,
  1.5056327351493116e-7,
];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const logGamma = (z) => {
  if (z < 0.5) {
    return Math.log(Math.PI) - Math.log(Math.sin(Math.PI * z)) - logGamma(1 - z);
  }

  let x = 0.9999999999998099;
  const adjusted = z - 1;
  for (let index = 0; index < LANCZOS_COEFFS.length; index += 1) {
    x += LANCZOS_COEFFS[index] / (adjusted + index + 1);
  }

  const t = adjusted + LANCZOS_COEFFS.length - 0.5;
  return LOG_SQRT_TWO_PI + (adjusted + 0.5) * Math.log(t) - t + Math.log(x);
};

const regularizedGammaP = (a, x) => {
  if (x <= 0) return 0;
  if (x < a + 1) {
    let ap = a;
    let sum = 1 / a;
    let del = sum;

    for (let n = 1; n <= 1000; n += 1) {
      ap += 1;
      del *= x / ap;
      sum += del;
      if (Math.abs(del) < Math.abs(sum) * 1e-15) {
        break;
      }
    }

    return sum * Math.exp(-x + a * Math.log(x) - logGamma(a));
  }

  return 1 - regularizedGammaQ(a, x);
};

const regularizedGammaQ = (a, x) => {
  if (x <= 0) return 1;
  if (x < a + 1) {
    return 1 - regularizedGammaP(a, x);
  }

  let b = x + 1 - a;
  let c = 1e30;
  let d = 1 / b;
  let h = d;

  for (let n = 1; n <= 1000; n += 1) {
    const an = -n * (n - a);
    b += 2;
    d = an * d + b;
    if (Math.abs(d) < 1e-30) d = 1e-30;
    c = b + an / c;
    if (Math.abs(c) < 1e-30) c = 1e-30;
    d = 1 / d;
    const del = d * c;
    h *= del;
    if (Math.abs(del - 1) < 1e-15) {
      break;
    }
  }

  return Math.exp(-x + a * Math.log(x) - logGamma(a)) * h;
};

const chiSquarePValue = (chiSquare, degreesOfFreedom) => {
  if (!Number.isFinite(chiSquare) || !Number.isFinite(degreesOfFreedom) || degreesOfFreedom <= 0) {
    return 1;
  }

  return clamp(regularizedGammaQ(degreesOfFreedom / 2, chiSquare / 2), 0, 1);
};

const round = (value, digits = 6) => {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

export const buildSimulationReport = ({ labels, counts, totalSpins }) => {
  const safeLabels = Array.isArray(labels) ? labels : [];
  const safeCounts = Array.isArray(counts) ? counts : [];
  const total = Math.max(0, Number(totalSpins) || 0);
  const segmentCount = safeLabels.length;

  if (!segmentCount || total <= 0) {
    return {
      totalSpins: total,
      segmentCount,
      expectedCount: 0,
      expectedProbability: 0,
      chiSquare: 0,
      degreesOfFreedom: 0,
      pValue: 1,
      passesAlpha: true,
      rows: [],
      largestPositiveDeviation: null,
      largestNegativeDeviation: null,
      maxAbsolutePercentageError: 0,
      standardDeviationCounts: 0,
    };
  }

  const expectedCount = total / segmentCount;
  const expectedProbability = 1 / segmentCount;

  const rows = safeLabels.map((label, index) => {
    const count = safeCounts[index] || 0;
    const observedProbability = count / total;
    const probabilityDifference = observedProbability - expectedProbability;
    const countDifference = count - expectedCount;
    const percentageDeviation = expectedProbability === 0
      ? 0
      : (probabilityDifference / expectedProbability) * 100;
    const standardError = Math.sqrt((expectedProbability * (1 - expectedProbability)) / total);
    const confidenceDelta = SIMULATION_CONFIG.confidenceZ * standardError;
    const confidenceIntervalLow = expectedProbability - confidenceDelta;
    const confidenceIntervalHigh = expectedProbability + confidenceDelta;
    const outsideConfidenceInterval =
      observedProbability < confidenceIntervalLow || observedProbability > confidenceIntervalHigh;

    return {
      index,
      label,
      count,
      expectedCount,
      observedProbability,
      expectedProbability,
      countDifference,
      probabilityDifference,
      percentageDeviation,
      standardError,
      confidenceIntervalLow,
      confidenceIntervalHigh,
      outsideConfidenceInterval,
    };
  });

  const chiSquare = rows.reduce((sum, row) => {
    const delta = row.count - expectedCount;
    return sum + ((delta * delta) / expectedCount);
  }, 0);

  const degreesOfFreedom = segmentCount - 1;
  const pValue = chiSquarePValue(chiSquare, degreesOfFreedom);
  const passesAlpha = pValue > SIMULATION_CONFIG.alpha;

  const sortedByDeviation = [...rows].sort(
    (left, right) => Math.abs(right.percentageDeviation) - Math.abs(left.percentageDeviation),
  );

  const largestPositiveDeviation = rows.reduce((best, row) => {
    if (!best || row.percentageDeviation > best.percentageDeviation) return row;
    return best;
  }, null);

  const largestNegativeDeviation = rows.reduce((best, row) => {
    if (!best || row.percentageDeviation < best.percentageDeviation) return row;
    return best;
  }, null);

  const meanCount = total / segmentCount;
  const variance = rows.reduce((sum, row) => {
    const delta = row.count - meanCount;
    return sum + (delta * delta);
  }, 0) / segmentCount;

  const maxAbsolutePercentageError = rows.reduce(
    (max, row) => Math.max(max, Math.abs(row.percentageDeviation)),
    0,
  );

  return {
    totalSpins: total,
    segmentCount,
    expectedCount,
    expectedProbability,
    chiSquare: round(chiSquare, 8),
    degreesOfFreedom,
    pValue: round(pValue, 8),
    passesAlpha,
    rows: sortedByDeviation.map((row) => ({
      ...row,
      observedProbability: round(row.observedProbability, 8),
      expectedProbability: round(row.expectedProbability, 8),
      countDifference: round(row.countDifference, 4),
      probabilityDifference: round(row.probabilityDifference, 8),
      percentageDeviation: round(row.percentageDeviation, 4),
      standardError: round(row.standardError, 8),
      confidenceIntervalLow: round(row.confidenceIntervalLow, 8),
      confidenceIntervalHigh: round(row.confidenceIntervalHigh, 8),
    })),
    largestPositiveDeviation: largestPositiveDeviation
      ? {
          ...largestPositiveDeviation,
          percentageDeviation: round(largestPositiveDeviation.percentageDeviation, 4),
        }
      : null,
    largestNegativeDeviation: largestNegativeDeviation
      ? {
          ...largestNegativeDeviation,
          percentageDeviation: round(largestNegativeDeviation.percentageDeviation, 4),
        }
      : null,
    maxAbsolutePercentageError: round(maxAbsolutePercentageError, 4),
    standardDeviationCounts: round(Math.sqrt(variance), 6),
  };
};